import { FC, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ChecklistInputType,
  ChecklistType,
  InquiryChecklist,
  fetchInquiryChecklist,
} from '../../api/inquiryChecklists';
import PageHeaderWithBack from '../../components/PageHeaderWithBack';

const formatInputType = (value: ChecklistInputType): string => {
  switch (value) {
    case 'boolean':
      return 'Yes / No';
    case 'text':
      return 'Free text';
    case 'single_select':
      return 'Single select';
    case 'multi_select':
      return 'Multi select';
    default:
      return value;
  }
};

const formatChecklistType = (value: ChecklistType): string => {
  switch (value) {
    case 'inspection':
      return 'Inspection';
    case 'pre_delivery':
      return 'Pre-delivery';
    default:
      return value;
  }
};

const VehicleInquiryChecklistViewer: FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checklist, setChecklist] = useState<InquiryChecklist | null>(null);

  useEffect(() => {
    if (!id) {
      setError('Checklist not found.');
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    setError(null);

    fetchInquiryChecklist(id)
      .then((response) => {
        if (!active) {
          return;
        }
        setChecklist(response);
      })
      .catch((err) => {
        if (!active) {
          return;
        }
        const message = err instanceof Error ? err.message : 'Unable to load checklist.';
        setError(message);
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [id]);

  const answerOptions = useMemo(() => checklist?.answerOptions ?? [], [checklist?.answerOptions]);

  const brandSummary = useMemo(() => {
    if (!checklist) {
      return '—';
    }
    if (!checklist.brands || checklist.brands.length === 0) {
      return 'All brands';
    }
    const names = checklist.brands
      .map((brand) => brand.name?.trim())
      .filter((name): name is string => Boolean(name));

    if (names.length === 0) {
      return `${checklist.brands.length} selected`;
    }

    return names.join(', ');
  }, [checklist]);

  const modelSummary = useMemo(() => {
    if (!checklist) {
      return '—';
    }
    if (!checklist.models || checklist.models.length === 0) {
      return checklist.brands && checklist.brands.length > 0
        ? 'All models for selected brands'
        : 'All models';
    }

    const names = checklist.models
      .map((model) => model.name?.trim())
      .filter((name): name is string => Boolean(name));

    if (names.length === 0) {
      return `${checklist.models.length} selected`;
    }

    return names.join(', ');
  }, [checklist]);

  const updatedAt = checklist ? new Date(checklist.updatedAt).toLocaleString() : '—';
  const createdAt = checklist ? new Date(checklist.createdAt).toLocaleString() : '—';

  return (
    <div className="rounded-2xl bg-white p-6 shadow-[0_20px_45px_-32px_rgba(37,40,85,0.55)]">
      <PageHeaderWithBack
        title="View checklist"
        description={<p className="mt-2 max-w-2xl text-sm text-slate-500">
          Review checklist details, including linked vehicle brands and models.
        </p>}
        onBack={() => navigate('/vehicle-management/checklists')}
        backLabel="Back to checklists"
      />

      {error ? (
        <p className="mt-4 text-sm text-danger">{error}</p>
      ) : null}

      {loading ? (
        <p className="mt-6 text-sm text-slate-500">Loading checklist…</p>
      ) : !checklist ? (
        <p className="mt-6 text-sm text-danger">Checklist not found.</p>
      ) : (
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-600">
            <h3 className="text-base font-semibold text-slate-700">Checklist overview</h3>
            <dl className="mt-3 space-y-3 text-sm">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Name</dt>
                <dd className="text-slate-700">{checklist.name}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Field type</dt>
                <dd className="text-slate-700">{formatInputType(checklist.inputType)}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Checklist type</dt>
                <dd className="text-slate-700">{formatChecklistType(checklist.type)}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Mandatory</dt>
                <dd className="text-slate-700">{checklist.isMandatory ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Description</dt>
                <dd className="whitespace-pre-line text-slate-700">
                  {checklist.description?.trim() ? checklist.description : '—'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Last updated</dt>
                <dd className="text-slate-700">{updatedAt}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Created</dt>
                <dd className="text-slate-700">{createdAt}</dd>
              </div>
            </dl>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-600">
            <h3 className="text-base font-semibold text-slate-700">Linked brands & models</h3>
            <dl className="mt-3 space-y-3 text-sm">
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Brands</dt>
                <dd className="text-slate-700">{brandSummary}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">Models</dt>
                <dd className="text-slate-700">{modelSummary}</dd>
              </div>
            </dl>
          </div>

          {(checklist.inputType === 'single_select' || checklist.inputType === 'multi_select') && (
            <div className="md:col-span-2 rounded-2xl border border-slate-200 bg-slate-50/70 p-4 text-sm text-slate-600">
              <h3 className="text-base font-semibold text-slate-700">Answer options</h3>
              {answerOptions.length ? (
                <ul className="mt-3 list-disc space-y-1 pl-5 text-slate-700">
                  {answerOptions.map((option, idx) => {
                    const label = typeof option === 'string' ? option : option.answer;
                    const color = typeof option === 'string' ? undefined : option.bgColor;
                    return (
                      <li key={`${label}-${idx}`} className="flex items-center gap-2">
                        <span
                          className="inline-block h-3 w-3 rounded"
                          style={{ backgroundColor: color || '#ffffff', outline: '1px solid #e5e7eb' }}
                        />
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <p className="mt-3 text-slate-500">No answer options configured.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VehicleInquiryChecklistViewer;
